import { createSlice } from '@reduxjs/toolkit';
import { initialState, RegistrationSliceState } from './state';
import { buildExtraReducers } from './extraReducers';

// Import reducers with Reducer suffix to avoid naming conflicts
import {
  initializeRegistration as initializeRegistrationReducer,
  updateRegistration as updateRegistrationReducer,
  clearRegistration as clearRegistrationReducer,
  addAddress as addAddressReducer,
  updateAddress as updateAddressReducer,
  removeAddress as removeAddressReducer,
  deleteAddress as deleteAddressReducer,
  markFieldAsEdited as markFieldAsEditedReducer,
  markAsSaved as markAsSavedReducer,
  discardChanges as discardChangesReducer,
  setValidationError as setValidationErrorReducer,
  clearValidationError as clearValidationErrorReducer,
  clearAllValidationErrors as clearAllValidationErrorsReducer,
} from './reducers';

/**
 * Registration slice for managing registration state
 */
const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    initializeRegistration: initializeRegistrationReducer,
    updateRegistration: updateRegistrationReducer,
    clearRegistration: clearRegistrationReducer,
    addAddress: addAddressReducer,
    updateAddress: updateAddressReducer,
    removeAddress: removeAddressReducer,
    deleteAddress: deleteAddressReducer,
    markFieldAsEdited: markFieldAsEditedReducer,
    markAsSaved: markAsSavedReducer,
    discardChanges: discardChangesReducer,
    setValidationError: setValidationErrorReducer,
    clearValidationError: clearValidationErrorReducer,
    clearAllValidationErrors: clearAllValidationErrorsReducer,
  },
  extraReducers: buildExtraReducers,
});

// Export actions with original names
export const {
  initializeRegistration,
  updateRegistration,
  clearRegistration,
  addAddress,
  updateAddress,
  removeAddress,
  deleteAddress,
  markFieldAsEdited,
  markAsSaved,
  discardChanges,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
} = registrationSlice.actions;

// Export actions with Action suffix for explicit naming
export const initializeRegistrationAction = registrationSlice.actions.initializeRegistration;
export const updateRegistrationAction = registrationSlice.actions.updateRegistration;
export const clearRegistrationAction = registrationSlice.actions.clearRegistration;
export const addAddressAction = registrationSlice.actions.addAddress;
export const updateAddressAction = registrationSlice.actions.updateAddress;
export const removeAddressAction = registrationSlice.actions.removeAddress;
export const deleteAddressAction = registrationSlice.actions.deleteAddress;
export const markFieldAsEditedAction = registrationSlice.actions.markFieldAsEdited;
export const markAsSavedAction = registrationSlice.actions.markAsSaved;
export const discardChangesAction = registrationSlice.actions.discardChanges;
export const setValidationErrorAction = registrationSlice.actions.setValidationError;
export const clearValidationErrorAction = registrationSlice.actions.clearValidationError;
export const clearAllValidationErrorsAction = registrationSlice.actions.clearAllValidationErrors;

// Export reducer as default
export default registrationSlice.reducer;

// Export state type
export type { RegistrationSliceState };

// Re-export selectors
export {
  selectCurrentRegistration,
  selectRegistrationIsEdited,
  selectEditedFields,
  selectValidationErrors,
  selectAddresses,
  selectName,
  selectDemographics,
  selectContactInfo,
  selectIdentifiers,
  selectIsFieldEdited,
  selectFieldValidationError,
} from './selectors';

// Re-export AddressType interface for backward compatibility
export type { AddressType } from './reducers/addressReducers';
